import { useState, useEffect, useRef } from "react";
import axios from "axios";
import useAuthStore from "@/store/authStore";
import PermButton from "@/components/PermButton";
import { uploadWithReceipt } from "@/lib/bulkUploadReceipt";
import { Plus, Search, Trash2, Edit, Building2, Package, TrendingDown, ChevronRight, X, Download, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import * as XLSX from 'xlsx';
import { Sparkline } from "@/components/Sparkline";
import { saveAs } from 'file-saver';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lazy per-row trend sparkline (Phase 4). Fetches the last 12 invoice
// prices for an RM on mount; renders a tiny SVG sparkline. The fetch
// is fire-and-forget so the comparison table renders immediately and
// each row's trend resolves as data arrives.
function RMTrendSparkline({ rmId, token }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API}/rm-prices/history/${encodeURIComponent(rmId)}?limit=12`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) setData(res.data || []);
      } catch (_e) {
        if (!cancelled) setData([]);
      }
    })();
    return () => { cancelled = true; };
  }, [rmId, token]);

  if (data === null) {
    return <span className="text-zinc-400 text-[10px]">…</span>;
  }
  if (data.length < 2) {
    return <span className="text-zinc-400 text-[10px]">no history</span>;
  }
  return <Sparkline values={data.map((d) => d.price)} type="cost" w={90} h={22} />;
}

// Allowed vendor-level payment terms (mirrors backend ALLOWED_PAYMENT_TERMS).
// Order matters — first item is the default for new vendors.
const PAYMENT_TERMS_OPTIONS = [
  { value: "DUE_ON_RECEIPT", label: "Due on Receipt" },
  { value: "NET_7", label: "Net 7" },
  { value: "NET_15", label: "Net 15" },
  { value: "NET_30", label: "Net 30" },
  { value: "NET_45", label: "Net 45" },
  { value: "NET_60", label: "Net 60" },
];
const DEFAULT_PAYMENT_TERMS = "DUE_ON_RECEIPT";

const VendorManagement = () => {
  const { token } = useAuthStore();
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorRMPrices, setVendorRMPrices] = useState([]);
  const [comparisonReport, setComparisonReport] = useState([]);
  const [rmVendors, setRmVendors] = useState([]);
  const [selectedRM, setSelectedRM] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [rmSearchQuery, setRmSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);
  const priceFileInputRef = useRef(null);
  
  const [showAddVendorDialog, setShowAddVendorDialog] = useState(false);
  const [showAddPriceDialog, setShowAddPriceDialog] = useState(false);
  const [showRMVendorsDialog, setShowRMVendorsDialog] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);

  const [vendorForm, setVendorForm] = useState({
    name: "", gst: "", address: "", poc: "", email: "", phone: "",
    payment_terms: DEFAULT_PAYMENT_TERMS,
    // Vendor classification (Feb 2026) — orthogonal to is_job_worker.
    vendor_type: "Standard",
    // Job Work Lifecycle Phase 1 (Jul 2026) — vendor flag + Zoho customer FK.
    is_job_worker: false,
    zoho_customer_id: "",
  });

  const [priceForm, setPriceForm] = useState({
    rm_id: "", price: "", currency: "INR", notes: ""
  });

  // ── FG price master (Feb 2026 — Phase 5 Vendor-Site FG) ────────────
  // A third tab mirroring the RM price tab but keyed on
  // (vendor_id, buyer_sku_id, branch?). branch=null = vendor-wide default.
  const [fgPrices, setFgPrices] = useState([]);
  const [fgVendorFilter, setFgVendorFilter] = useState(""); // vendor_id or ""
  const [fgSkuFilter, setFgSkuFilter] = useState("");
  const [showAddFgPriceDialog, setShowAddFgPriceDialog] = useState(false);
  const [editingFgPrice, setEditingFgPrice] = useState(null); // full row when editing
  const [fgPriceForm, setFgPriceForm] = useState({
    vendor_id: "", buyer_sku_id: "", branch: "", price: "", currency: "INR", notes: "",
  });
  const [fgAvailableBranches, setFgAvailableBranches] = useState([]);
  const [availableBuyerSkus, setAvailableBuyerSkus] = useState([]);
  const [fgUploadResult, setFgUploadResult] = useState(null);
  const fgBulkFileRef = useRef(null);

  // Available RMs for dropdown
  const [availableRMs, setAvailableRMs] = useState([]);
  const [rmSearch, setRmSearch] = useState("");

  useEffect(() => {
    fetchVendors();
    fetchComparisonReport();
    fetchAvailableRMs();
    fetchFgPrices();
    fetchAvailableBuyerSkus();
    fetchFgBranches();
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data);
    } catch (error) {
      toast.error("Failed to fetch vendors");
    }
  };

  const fetchComparisonReport = async () => {
    try {
      const response = await axios.get(`${API}/vendor-rm-prices/comparison`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComparisonReport(response.data);
    } catch (error) {
      console.error("Failed to fetch comparison report", error);
      toast.error(error.response?.data?.detail || "Failed to load price comparison");
    }
  };

  const fetchAvailableRMs = async () => {
    try {
      const response = await axios.get(`${API}/raw-materials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableRMs(response.data);
    } catch (error) {
      console.error("Failed to fetch RMs");
    }
  };

  // ── FG Price handlers ──────────────────────────────────────────────

  const fetchFgPrices = async (vendorId, skuId) => {
    try {
      const params = {};
      if (vendorId) params.vendor_id = vendorId;
      if (skuId) params.buyer_sku_id = skuId;
      const res = await axios.get(`${API}/vendor-fg-prices`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setFgPrices(res.data || []);
    } catch (e) {
      console.error("Failed to fetch FG prices", e);
    }
  };

  const fetchAvailableBuyerSkus = async () => {
    try {
      // Reuse the /search endpoint (light payload) to populate the SKU picker.
      const res = await axios.get(`${API}/bom-lookup/search`, {
        params: { limit: 5000 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailableBuyerSkus(res.data?.results || res.data || []);
    } catch (e) {
      console.error("Failed to fetch buyer SKUs", e);
    }
  };

  const fetchFgBranches = async () => {
    try {
      const res = await axios.get(`${API}/branches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // VENDOR_SITE branches only — FG prices are only meaningful at
      // vendor sites (spec §CONTEXT). Blank branch = vendor-wide default.
      const all = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setFgAvailableBranches(all.filter(b => b.branch_type === "VENDOR_SITE"));
    } catch (e) {
      console.error("Failed to fetch branches", e);
    }
  };

  const openAddFgPrice = () => {
    setEditingFgPrice(null);
    setFgPriceForm({ vendor_id: "", buyer_sku_id: "", branch: "", price: "", currency: "INR", notes: "" });
    setShowAddFgPriceDialog(true);
  };

  const openEditFgPrice = (row) => {
    setEditingFgPrice(row);
    setFgPriceForm({
      vendor_id: row.vendor_id || "",
      buyer_sku_id: row.buyer_sku_id || "",
      branch: row.branch || "",
      price: String(row.price ?? ""),
      currency: row.currency || "INR",
      notes: row.notes || "",
    });
    setShowAddFgPriceDialog(true);
  };

  const handleSaveFgPrice = async () => {
    if (!fgPriceForm.vendor_id || !fgPriceForm.buyer_sku_id || !fgPriceForm.price) {
      toast.error("Vendor, Buyer SKU and Price are required");
      return;
    }
    try {
      const body = {
        vendor_id: fgPriceForm.vendor_id,
        buyer_sku_id: fgPriceForm.buyer_sku_id,
        branch: fgPriceForm.branch || null, // blank → vendor-wide default
        price: parseFloat(fgPriceForm.price),
        currency: fgPriceForm.currency || "INR",
        notes: fgPriceForm.notes || null,
      };
      if (editingFgPrice) {
        await axios.patch(`${API}/vendor-fg-prices/${editingFgPrice.id}`,
          { price: body.price, currency: body.currency, notes: body.notes },
          { headers: { Authorization: `Bearer ${token}` } });
        toast.success("FG price updated");
      } else {
        await axios.post(`${API}/vendor-fg-prices`, body, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("FG price added");
      }
      setShowAddFgPriceDialog(false);
      fetchFgPrices(fgVendorFilter, fgSkuFilter);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save FG price");
    }
  };

  const handleDeleteFgPrice = async (id) => {
    if (!window.confirm("Delete this FG price?")) return;
    try {
      await axios.delete(`${API}/vendor-fg-prices/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("FG price deleted");
      fetchFgPrices(fgVendorFilter, fgSkuFilter);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete FG price");
    }
  };

  const downloadFgTemplate = async () => {
    // `window.open` in a new tab sends no Authorization header, so the
    // endpoint's `get_current_user` guard 401s ("Not authenticated" —
    // exactly what showed up in prod on 2026-07-01). Fetch it as an
    // authenticated blob and save through the same file-saver path
    // `exportFgPrices` uses.
    try {
      const res = await axios.get(`${API}/vendor-fg-prices/template`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      saveAs(new Blob([res.data]), "vendor_fg_prices_template.xlsx");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to download template");
    }
  };

  const handleFgBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFgUploadResult(null);
    // NB: the FG bulk endpoint is `/bulk` (not `/bulk-upload` like RM).
    // Use the shared receipt helper's positional-args convention (mirrors
    // `handleBulkUpload` at line ~525 and `handlePriceBulkUpload` at ~564
    // — the object-arg form silently dropped the file and auth header
    // because `uploadWithReceipt(url, formData, opts)` takes 3 positional
    // args and reads auth from `opts.axiosConfig.headers`).
    const formData = new FormData();
    formData.append("file", file);
    try {
      const data = await uploadWithReceipt(
        `${API}/vendor-fg-prices/bulk`,
        formData,
        {
          axiosConfig: { headers: { Authorization: `Bearer ${token}` } },
          showToast: false,
        },
      );
      setFgUploadResult(data);
      const summary = data.summary || {};
      const inserted = summary.success ?? data.inserted ?? 0;
      const skipped = summary.skipped ?? 0;
      const errored = summary.errors ?? 0;
      if (inserted > 0) {
        const parts = [`${inserted} inserted`];
        if (skipped) parts.push(`${skipped} skipped`);
        if (errored) parts.push(`${errored} errored`);
        toast.success(`${parts.join(", ")} — Receipt downloaded.`);
      } else if (skipped || errored) {
        toast.warning(
          `No FG prices inserted (${skipped} skipped, ${errored} errored) — Receipt downloaded.`,
          { duration: 8000 },
        );
      } else {
        toast.info("Uploaded file had no processable rows.");
      }
      fetchFgPrices(fgVendorFilter, fgSkuFilter);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk upload failed");
    } finally {
      if (fgBulkFileRef.current) fgBulkFileRef.current.value = "";
    }
  };

  const exportFgPrices = async () => {
    try {
      const res = await axios.get(`${API}/vendor-fg-prices/export`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      saveAs(new Blob([res.data]), "vendor_fg_prices.xlsx");
      toast.success("Exported");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  const filteredFgPrices = fgPrices.filter(r => {
    if (fgVendorFilter && r.vendor_id !== fgVendorFilter) return false;
    if (fgSkuFilter) {
      const s = fgSkuFilter.toLowerCase();
      if (!(r.buyer_sku_id || "").toLowerCase().includes(s) &&
          !(r.buyer_sku_name || "").toLowerCase().includes(s)) return false;
    }
    return true;
  });


  const fetchVendorDetails = async (vendorId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/vendors/${vendorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedVendor(response.data.vendor);
      setVendorRMPrices(response.data.rm_prices);
    } catch (error) {
      toast.error("Failed to fetch vendor details");
    } finally {
      setLoading(false);
    }
  };

  const fetchRMVendors = async (rmId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/vendor-rm-prices/by-rm/${rmId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRmVendors(response.data);
      setSelectedRM(rmId);
      setShowRMVendorsDialog(true);
    } catch (error) {
      toast.error("Failed to fetch vendors for RM");
    } finally {
      setLoading(false);
    }
  };

  const handleAddVendor = async () => {
    if (!vendorForm.name) {
      toast.error("Vendor name is required");
      return;
    }
    if (!vendorForm.payment_terms) {
      toast.error("Payment Terms is required");
      return;
    }

    try {
      if (editingVendor) {
        await axios.put(`${API}/vendors/${editingVendor.id}`, vendorForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Vendor updated");
      } else {
        await axios.post(`${API}/vendors`, vendorForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Vendor added");
      }
      
      setShowAddVendorDialog(false);
      resetVendorForm();
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save vendor");
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm("Delete this vendor and all associated prices?")) return;
    
    try {
      await axios.delete(`${API}/vendors/${vendorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Vendor deleted");
      fetchVendors();
      setSelectedVendor(null);
      fetchComparisonReport();
    } catch (error) {
      toast.error("Failed to delete vendor");
    }
  };

  const handleAddPrice = async () => {
    if (!priceForm.rm_id || !priceForm.price) {
      toast.error("RM ID and Price are required");
      return;
    }

    try {
      // Backend `vendor-rm-prices` lookup uses the *business* vendor_id
      // (e.g. VND_208), not the Mongo doc UUID. Falls back to .id only if
      // vendor_id is missing on the loaded record.
      const vendorBizId = selectedVendor?.vendor_id || selectedVendor?.id;
      await axios.post(`${API}/vendor-rm-prices`, {
        vendor_id: vendorBizId,
        rm_id: priceForm.rm_id,
        price: parseFloat(priceForm.price),
        currency: priceForm.currency,
        notes: priceForm.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Price added/updated");
      setShowAddPriceDialog(false);
      resetPriceForm();
      fetchVendorDetails(selectedVendor.id);
      fetchComparisonReport();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add price");
    }
  };

  const handleDeletePrice = async (priceId) => {
    if (!priceId) {
      toast.error("Missing price id — refresh and try again");
      return;
    }
    if (!window.confirm("Remove this price mapping?")) return;

    try {
      await axios.delete(`${API}/vendor-rm-prices/${priceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Price removed");
      if (selectedVendor?.id) fetchVendorDetails(selectedVendor.id);
      fetchComparisonReport();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove price");
    }
  };

  const resetVendorForm = () => {
    setVendorForm({
      name: "", gst: "", address: "", poc: "", email: "", phone: "",
      payment_terms: DEFAULT_PAYMENT_TERMS,
      vendor_type: "Standard",
      is_job_worker: false, zoho_customer_id: "",
    });
    setEditingVendor(null);
  };

  const resetPriceForm = () => {
    setPriceForm({ rm_id: "", price: "", currency: "INR", notes: "" });
    setRmSearch("");
  };

  const openEditVendor = (vendor) => {
    setVendorForm({
      name: vendor.name,
      gst: vendor.gst || "",
      address: vendor.address || "",
      poc: vendor.poc || "",
      email: vendor.email || "",
      phone: vendor.phone || "",
      payment_terms: vendor.payment_terms || DEFAULT_PAYMENT_TERMS,
      vendor_type: vendor.vendor_type || "Standard",
      is_job_worker: Boolean(vendor.is_job_worker),
      zoho_customer_id: vendor.zoho_customer_id || "",
    });
    setEditingVendor(vendor);
    setShowAddVendorDialog(true);
  };

  const exportComparisonReport = () => {
    const ws = XLSX.utils.json_to_sheet(comparisonReport.map(r => ({
      'RM ID': r.rm_id,
      'Category': r.rm_category,
      'Lowest Price': r.lowest_price,
      'Currency': r.currency,
      'Best Vendor': r.lowest_vendor_name,
      'Total Vendors': r.total_vendors
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Price Comparison');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), 'rm_price_comparison.xlsx');
    toast.success("Exported to Excel");
  };

  const filteredVendors = vendors.filter(v => 
    v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.gst?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.vendor_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setLoading(true);
    try {
      const data = await uploadWithReceipt(`${API}/vendors/bulk-upload`, formData, {
        axiosConfig: { headers: { Authorization: `Bearer ${token}` } },
        showToast: false,
      });
      setUploadResult(data);

      const { created = 0, updated = 0, unchanged = 0 } = data;
      const summary = data.summary || {};
      if (created > 0 || updated > 0) {
        const parts = [];
        if (created > 0) parts.push(`${created} created`);
        if (updated > 0) parts.push(`${updated} updated`);
        if (unchanged > 0) parts.push(`${unchanged} unchanged`);
        toast.success(parts.join(", ") + " — Receipt downloaded.");
      } else if (unchanged > 0) {
        toast.info(`No changes — ${unchanged} vendors already up to date. Receipt downloaded.`);
      }
      if (summary.errors > 0) {
        toast.warning(`${summary.errors} row(s) errored — see receipt for details.`, { duration: 7000 });
      }

      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const handlePriceBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setLoading(true);
    try {
      const data = await uploadWithReceipt(
        `${API}/vendor-rm-prices/bulk-upload?mode=upsert`,
        formData,
        {
          axiosConfig: { headers: { Authorization: `Bearer ${token}` } },
          showToast: false,
        }
      );
      setUploadResult({
        created: data.created,
        skipped: data.updated,
        total_errors: data.error_count,
        errors: data.errors,
        _type: 'price',
      });
      const summary = data.summary || {};
      if ((data.created || 0) + (data.updated || 0) > 0) {
        toast.success(`${data.created} new, ${data.updated} updated — Receipt downloaded.`);
      }
      if (summary.errors > 0) {
        toast.warning(`${summary.errors} row(s) errored — see receipt for details.`);
      }
      if (selectedVendor) fetchVendorDetails(selectedVendor.vendor_id);
      fetchComparisonReport();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Price upload failed");
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const downloadPriceTemplate = async () => {
    // `window.open` in a new tab sends no Authorization header, so the
    // endpoint's `get_current_user` guard 401s in prod. Fetch it as an
    // authenticated blob and save through file-saver — mirrors the FG
    // template fix (see downloadFgTemplate above).
    try {
      const res = await axios.get(`${API}/vendor-rm-prices/template`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      saveAs(new Blob([res.data]), 'vendor_rm_prices_template.xlsx');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to download template');
    }
  };

  const downloadVendorTemplate = async () => {
    // Use backend template (multi-address aware — Feb 2026, Phase 1).
    // Authenticated blob download to avoid 401s in prod (same reason as
    // downloadPriceTemplate / downloadFgTemplate).
    try {
      const res = await axios.get(`${API}/vendors/template`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      saveAs(new Blob([res.data]), 'vendors_template.xlsx');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to download template');
    }
  };

  const exportVendors = () => {
    const ptLabel = (code) => (PAYMENT_TERMS_OPTIONS.find(p => p.value === code)?.label) || code || 'Due on Receipt';
    const ws = XLSX.utils.json_to_sheet(vendors.map(v => ({
      'Vendor ID': v.vendor_id,
      'Name': v.name,
      'Payment Terms': ptLabel(v.payment_terms),
      'GST': v.gst || '',
      'Address': v.address || '',
      'POC': v.poc || '',
      'Email': v.email || '',
      'Phone': v.phone || ''
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Vendors');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), 'vendors_export.xlsx');
    toast.success("Exported to Excel");
  };

  const exportVendorRMPrices = async () => {
    try {
      const response = await axios.get(`${API}/vendor-rm-prices/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vendor_rm_prices_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Vendor × RM price mappings exported");
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("No vendor-RM price mappings exist yet");
      } else {
        toast.error(error.response?.data?.detail || "Export failed");
      }
    }
  };

  const filteredComparison = comparisonReport.filter(r =>
    r.rm_id.toLowerCase().includes(rmSearchQuery.toLowerCase()) ||
    r.rm_category?.toLowerCase().includes(rmSearchQuery.toLowerCase())
  );

  const filteredRMsForDropdown = availableRMs.filter(rm =>
    rm.rm_id.toLowerCase().includes(rmSearch.toLowerCase())
  ).slice(0, 50);

  return (
    <div className="p-6 md:p-8" data-testid="vendor-management-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black tracking-tight uppercase">Vendor Management</h1>
          <p className="text-sm text-muted-foreground mt-1 font-mono">
            Manage vendors and RM pricing • {vendors.length} vendors
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="secondary" onClick={downloadVendorTemplate} className="uppercase text-xs">
            <Download className="w-4 h-4 mr-2" /> Vendor Template
          </Button>
          <Button variant="secondary" onClick={downloadPriceTemplate} className="uppercase text-xs" data-testid="download-price-template">
            <Download className="w-4 h-4 mr-2" /> Price Template
          </Button>
          <Button variant="secondary" onClick={exportVendors} className="uppercase text-xs">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
          <Button 
            variant="secondary" 
            onClick={exportVendorRMPrices} 
            className="uppercase text-xs"
            data-testid="export-vendor-rm-prices"
          >
            <Download className="w-4 h-4 mr-2" /> Export Prices
          </Button>
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleBulkUpload} 
            accept=".xlsx,.xls"
            className="hidden"
          />
          <input
            type="file"
            ref={priceFileInputRef}
            onChange={handlePriceBulkUpload}
            accept=".xlsx,.xls"
            className="hidden"
          />
          <PermButton
            entity="Vendor" action="CREATE"
            onClick={() => fileInputRef.current.click()}
            disabled={loading}
            className="uppercase text-xs"
            variant="outline"
          >
            <Upload className="w-4 h-4 mr-2" /> Bulk Upload Vendors
          </PermButton>
          <PermButton
            entity="VendorRMPrice" action="CREATE"
            onClick={() => priceFileInputRef.current.click()}
            disabled={loading}
            className="uppercase text-xs"
            data-testid="bulk-upload-prices-btn"
          >
            <Upload className="w-4 h-4 mr-2" /> Bulk Upload Prices
          </PermButton>
        </div>
      </div>

      {/* Upload Result */}
      {uploadResult && (
        <div className="mb-6 p-4 bg-zinc-50 border border-zinc-200 rounded-sm" data-testid="upload-result">
          <h3 className="font-bold text-sm mb-2">
            Last Upload Result {uploadResult._type === 'price' ? '(Vendor RM Prices)' : '(Vendors)'}
          </h3>
          <div className="grid grid-cols-3 gap-4 text-sm font-mono">
            <div><span className="text-green-600 font-bold">{uploadResult.created}</span> {uploadResult._type === 'price' ? 'new' : 'created'}</div>
            <div><span className="text-yellow-600 font-bold">{uploadResult.skipped}</span> {uploadResult._type === 'price' ? 'updated' : 'skipped'}</div>
            <div><span className="text-red-600 font-bold">{uploadResult.total_errors}</span> errors</div>
          </div>
          {uploadResult.errors && uploadResult.errors.length > 0 && (
            <details className="mt-3 text-xs">
              <summary className="cursor-pointer text-red-600 font-medium">View first {uploadResult.errors.length} errors</summary>
              <ul className="mt-2 space-y-0.5 font-mono text-red-700 max-h-40 overflow-y-auto">
                {uploadResult.errors.map((err, idx) => (<li key={idx}>· {err}</li>))}
              </ul>
            </details>
          )}
        </div>
      )}

      <Tabs defaultValue="vendors" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="vendors" className="uppercase text-xs">
            <Building2 className="w-4 h-4 mr-2" />
            Vendors
          </TabsTrigger>
          <TabsTrigger value="comparison" className="uppercase text-xs">
            <TrendingDown className="w-4 h-4 mr-2" />
            Price Comparison
          </TabsTrigger>
          <TabsTrigger value="fg-prices" className="uppercase text-xs" data-testid="fg-prices-tab">
            <Package className="w-4 h-4 mr-2" />
            FG Prices
          </TabsTrigger>
        </TabsList>

        {/* Vendors Tab */}
        <TabsContent value="vendors">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Vendor List */}
            <div className="lg:col-span-1 border border-border bg-white rounded-sm">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h2 className="font-bold uppercase text-sm">Vendors</h2>
                <Dialog open={showAddVendorDialog} onOpenChange={(open) => {
                  setShowAddVendorDialog(open);
                  if (!open) resetVendorForm();
                }}>
                  <DialogTrigger asChild>
                    <PermButton entity="Vendor" action="CREATE" size="sm" className="uppercase text-xs" data-testid="add-vendor-btn">
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </PermButton>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-bold uppercase">
                        {editingVendor ? "Edit Vendor" : "Add Vendor"}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label>Vendor Name *</Label>
                        <Input 
                          value={vendorForm.name}
                          onChange={(e) => setVendorForm({...vendorForm, name: e.target.value})}
                          placeholder="Vendor name"
                        />
                      </div>
                      <div>
                        <Label>GST Number</Label>
                        <Input 
                          value={vendorForm.gst}
                          onChange={(e) => setVendorForm({...vendorForm, gst: e.target.value})}
                          placeholder="GSTIN"
                        />
                      </div>
                      <div>
                        <Label>Address</Label>
                        <Textarea 
                          value={vendorForm.address}
                          onChange={(e) => setVendorForm({...vendorForm, address: e.target.value})}
                          placeholder="Full address"
                          rows={2}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Point of Contact</Label>
                          <Input 
                            value={vendorForm.poc}
                            onChange={(e) => setVendorForm({...vendorForm, poc: e.target.value})}
                            placeholder="Contact person"
                          />
                        </div>
                        <div>
                          <Label>Phone</Label>
                          <Input 
                            value={vendorForm.phone}
                            onChange={(e) => setVendorForm({...vendorForm, phone: e.target.value})}
                            placeholder="Phone number"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input 
                          type="email"
                          value={vendorForm.email}
                          onChange={(e) => setVendorForm({...vendorForm, email: e.target.value})}
                          placeholder="Email address"
                        />
                      </div>
                      <div>
                        <Label>Payment Terms *</Label>
                        <Select
                          value={vendorForm.payment_terms || DEFAULT_PAYMENT_TERMS}
                          onValueChange={(v) => setVendorForm({...vendorForm, payment_terms: v})}
                        >
                          <SelectTrigger data-testid="vendor-payment-terms-select">
                            <SelectValue placeholder="Select payment terms" />
                          </SelectTrigger>
                          <SelectContent>
                            {PAYMENT_TERMS_OPTIONS.map(pt => (
                              <SelectItem key={pt.value} value={pt.value} data-testid={`pt-option-${pt.value}`}>
                                {pt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                          Auto-populated in RM Inward when this vendor is selected.
                        </p>
                      </div>
                      <div>
                        <Label>Vendor Type</Label>
                        <Select
                          value={vendorForm.vendor_type || "Standard"}
                          onValueChange={(v) => setVendorForm({...vendorForm, vendor_type: v})}
                        >
                          <SelectTrigger data-testid="vendor-form-type-select">
                            <SelectValue placeholder="Select vendor type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Standard" data-testid="vendor-type-option-Standard">Standard</SelectItem>
                            <SelectItem value="Transporter" data-testid="vendor-type-option-Transporter">Transporter</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                          Classification used by downstream pickers (e.g. Dispatch Lot transporter selection).
                        </p>
                      </div>
                      {/* Job Work Lifecycle Phase 1 (Jul 2026) — a job-work
                          vendor holds Bidso stock temporarily and must have
                          a Zoho customer contact (Zoho DC push is
                          customer-only). Both fields are ops-managed. */}
                      <div className="border-t pt-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="text-sm font-bold uppercase">Job Worker Vendor</Label>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Enable for vendors who receive material via Job Work Delivery Challans.
                            </p>
                          </div>
                          <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={Boolean(vendorForm.is_job_worker)}
                              onChange={(e) => setVendorForm({...vendorForm, is_job_worker: e.target.checked})}
                              className="w-4 h-4 accent-orange-500"
                              data-testid="vendor-is-job-worker-toggle"
                            />
                            <span className="text-xs uppercase font-mono">
                              {vendorForm.is_job_worker ? "Yes" : "No"}
                            </span>
                          </label>
                        </div>
                        {vendorForm.is_job_worker && (
                          <div>
                            <Label>Zoho Customer ID *</Label>
                            <Input
                              value={vendorForm.zoho_customer_id}
                              onChange={(e) => setVendorForm({...vendorForm, zoho_customer_id: e.target.value})}
                              placeholder="e.g. 1234567890000000123"
                              data-testid="vendor-zoho-customer-id"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Zoho Books contact_id of this vendor's customer-role contact.
                              Required to push Job Work Delivery Challans.
                            </p>
                          </div>
                        )}
                      </div>
                      <Button onClick={handleAddVendor} className="w-full uppercase text-xs" data-testid="save-vendor-btn">
                        {editingVendor ? "Update Vendor" : "Add Vendor"}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              
              <div className="p-2">
                <Input 
                  placeholder="Search vendors..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="mb-2"
                />
              </div>
              
              <div className="max-h-[500px] overflow-y-auto">
                {filteredVendors.map((vendor) => (
                  <div 
                    key={vendor.id}
                    className={`p-4 border-b border-zinc-100 cursor-pointer hover:bg-zinc-50 flex items-center justify-between ${selectedVendor?.id === vendor.id ? 'bg-zinc-100' : ''}`}
                    onClick={() => fetchVendorDetails(vendor.id)}
                  >
                    <div>
                      <div className="text-xs font-mono text-primary font-bold">{vendor.vendor_id || 'N/A'}</div>
                      <div className="font-bold text-sm">{vendor.name}</div>
                      <div className="text-xs text-zinc-500 font-mono">{vendor.gst || 'No GST'}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-400" />
                  </div>
                ))}
                {filteredVendors.length === 0 && (
                  <div className="p-4 text-center text-sm text-zinc-500">No vendors found</div>
                )}
              </div>
            </div>

            {/* Vendor Details */}
            <div className="lg:col-span-2 border border-border bg-white rounded-sm">
              {selectedVendor ? (
                <>
                  <div className="p-4 border-b border-border flex items-center justify-between">
                    <div>
                      <div className="text-xs font-mono text-primary font-bold mb-1">{selectedVendor.vendor_id || 'N/A'}</div>
                      <h2 className="font-bold text-lg">{selectedVendor.name}</h2>
                      <div className="text-xs text-zinc-500 font-mono">
                        {selectedVendor.gst && `GST: ${selectedVendor.gst}`}
                        {selectedVendor.phone && ` • ${selectedVendor.phone}`}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <PermButton entity="Vendor" action="UPDATE" size="sm" variant="outline" onClick={() => openEditVendor(selectedVendor)} data-testid="edit-vendor-btn">
                        <Edit className="w-4 h-4" />
                      </PermButton>
                      <PermButton entity="Vendor" action="DELETE" size="sm" variant="outline" onClick={() => handleDeleteVendor(selectedVendor.id)} data-testid="delete-vendor-btn">
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </PermButton>
                      <Dialog open={showAddPriceDialog} onOpenChange={(open) => {
                        setShowAddPriceDialog(open);
                        if (!open) resetPriceForm();
                      }}>
                        <DialogTrigger asChild>
                          <PermButton entity="VendorRMPrice" action="CREATE" size="sm" className="uppercase text-xs" data-testid="add-price-btn">
                            <Plus className="w-4 h-4 mr-1" /> Add Price
                          </PermButton>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle className="font-bold uppercase">Add RM Price</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <Label>Search RM ID</Label>
                              <Input 
                                value={rmSearch}
                                onChange={(e) => setRmSearch(e.target.value)}
                                placeholder="Type to search RM..."
                                className="mb-2"
                              />
                              <Label>Select RM *</Label>
                              <select
                                value={priceForm.rm_id}
                                onChange={(e) => setPriceForm({...priceForm, rm_id: e.target.value})}
                                className="flex h-10 w-full rounded-sm border border-input bg-transparent px-3 py-2 text-sm font-mono"
                              >
                                <option value="">Select RM ({filteredRMsForDropdown.length} shown)</option>
                                {filteredRMsForDropdown.map(rm => (
                                  <option key={rm.rm_id} value={rm.rm_id}>
                                    {rm.rm_id} - {rm.category}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label>Price *</Label>
                                <Input 
                                  type="number"
                                  step="0.01"
                                  value={priceForm.price}
                                  onChange={(e) => setPriceForm({...priceForm, price: e.target.value})}
                                  placeholder="0.00"
                                />
                              </div>
                              <div>
                                <Label>Currency</Label>
                                <select
                                  value={priceForm.currency}
                                  onChange={(e) => setPriceForm({...priceForm, currency: e.target.value})}
                                  className="flex h-10 w-full rounded-sm border border-input bg-transparent px-3 py-2 text-sm"
                                >
                                  <option value="INR">INR</option>
                                  <option value="USD">USD</option>
                                </select>
                              </div>
                            </div>
                            <div>
                              <Label>Notes</Label>
                              <Textarea 
                                value={priceForm.notes}
                                onChange={(e) => setPriceForm({...priceForm, notes: e.target.value})}
                                placeholder="Additional notes..."
                                rows={2}
                              />
                            </div>
                            <Button onClick={handleAddPrice} className="w-full uppercase text-xs">
                              Add/Update Price
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                  
                  {/* Vendor Info */}
                  {(selectedVendor.address || selectedVendor.poc || selectedVendor.email) && (
                    <div className="p-4 bg-zinc-50 border-b border-zinc-200 text-sm">
                      {selectedVendor.address && <div className="mb-1">{selectedVendor.address}</div>}
                      {selectedVendor.poc && <div>POC: {selectedVendor.poc}</div>}
                      {selectedVendor.email && <div>Email: {selectedVendor.email}</div>}
                    </div>
                  )}
                  
                  {/* RM Prices Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-zinc-50 border-b border-zinc-200">
                        <tr>
                          <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase">RM ID</th>
                          <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase">Category</th>
                          <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase">Price</th>
                          <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase">Notes</th>
                          <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vendorRMPrices.map((price) => (
                          <tr key={price.id} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                            <td className="p-4 align-middle font-mono text-sm font-bold text-primary cursor-pointer hover:underline" onClick={() => fetchRMVendors(price.rm_id)}>
                              {price.rm_id}
                            </td>
                            <td className="p-4 align-middle">
                              <span className="text-xs font-mono px-2 py-1 bg-zinc-100 rounded">
                                {price.rm_category}
                              </span>
                            </td>
                            <td className="p-4 align-middle font-mono text-sm font-bold text-green-600">
                              {price.currency} {price.price.toFixed(2)}
                            </td>
                            <td className="p-4 align-middle text-xs text-zinc-500">
                              {price.notes || '-'}
                            </td>
                            <td className="p-4 align-middle">
                              <PermButton entity="VendorRMPrice" action="DELETE" variant="ghost" size="sm" onClick={() => handleDeletePrice(price.id)} data-testid={`delete-price-${price.id}`}>
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </PermButton>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {vendorRMPrices.length === 0 && (
                      <div className="p-8 text-center text-zinc-500 text-sm">
                        No RM prices added yet. Click "Add Price" to start.
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="p-12 text-center text-zinc-500">
                  <Building2 className="w-12 h-12 mx-auto mb-4 text-zinc-300" />
                  <div className="text-sm">Select a vendor to view details and RM prices</div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Price Comparison Tab */}
        <TabsContent value="comparison">
          <div className="mb-4 flex items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input 
                placeholder="Search by RM ID or Category..."
                value={rmSearchQuery}
                onChange={(e) => setRmSearchQuery(e.target.value)}
                className="pl-10 font-mono"
              />
            </div>
            <Button variant="secondary" onClick={exportComparisonReport} className="uppercase text-xs">
              <Download className="w-4 h-4 mr-2" /> Export Report
            </Button>
          </div>

          <div className="border border-border bg-white rounded-sm overflow-hidden">
            <div className="p-4 border-b border-border bg-zinc-50">
              <h3 className="font-bold uppercase text-sm flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-green-600" />
                Lowest Price per RM (for Procurement)
              </h3>
              <p className="text-xs text-zinc-500 mt-1">Click on RM ID to see all vendors and their prices</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr>
                    <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase">RM ID</th>
                    <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase">Category</th>
                    <th className="h-10 px-4 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase">Lowest Price</th>
                    <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase">Best Vendor</th>
                    <th className="h-10 px-4 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase" title="Most recent invoice price (rm_prices_history)">Last Purchase</th>
                    <th className="h-10 px-4 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase" title="Mean of invoice prices over the last 90 days">90d Avg</th>
                    <th className="h-10 px-4 text-right align-middle font-mono text-xs font-medium text-zinc-500 uppercase" title="Stock-weighted Moving Weighted Average Cost across branches">Branch WAC</th>
                    <th className="h-10 px-4 text-center align-middle font-mono text-xs font-medium text-zinc-500 uppercase">Trend (last 12)</th>
                    <th className="h-10 px-4 text-left align-middle font-mono text-xs font-medium text-zinc-500 uppercase">Total Vendors</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredComparison.map((item) => (
                    <tr key={item.rm_id} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`comparison-row-${item.rm_id}`}>
                      <td className="p-4 align-middle font-mono text-sm font-bold text-primary cursor-pointer hover:underline" onClick={() => fetchRMVendors(item.rm_id)} data-testid={`comparison-rm-${item.rm_id}`}>
                        {item.rm_id}
                      </td>
                      <td className="p-4 align-middle">
                        <span className="text-xs font-mono px-2 py-1 bg-zinc-100 rounded">
                          {item.rm_category}
                        </span>
                      </td>
                      <td className="p-4 align-middle font-mono text-sm font-bold text-green-600 text-right">
                        {item.currency} {item.lowest_price.toFixed(2)}
                      </td>
                      <td className="p-4 align-middle text-sm">
                        {item.lowest_vendor_name}
                      </td>
                      <td className="p-4 align-middle font-mono text-xs text-right" data-testid={`comparison-last-purchase-${item.rm_id}`}>
                        {item.last_purchase_price != null && item.last_purchase_price > 0 ? (
                          <>
                            <div className={item.is_stale_90d ? "text-amber-700" : "text-zinc-700"}>
                              ₹{Number(item.last_purchase_price).toFixed(2)}
                            </div>
                            <div className="text-[10px] text-zinc-400">
                              {item.last_purchase_date ? new Date(item.last_purchase_date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: '2-digit' }) : '—'}
                              {item.is_stale_90d && (
                                <span className="ml-1 inline-block px-1 py-0 rounded-sm bg-amber-100 text-amber-800 text-[9px]" title="No purchase in last 90 days">stale</span>
                              )}
                            </div>
                          </>
                        ) : (
                          <span className="text-zinc-400">—</span>
                        )}
                      </td>
                      <td className="p-4 align-middle font-mono text-xs text-zinc-700 text-right">
                        {item.avg_90d_unweighted != null && item.avg_90d_unweighted > 0
                          ? `₹${Number(item.avg_90d_unweighted).toFixed(2)}`
                          : <span className="text-zinc-400">—</span>}
                      </td>
                      <td className="p-4 align-middle font-mono text-xs text-zinc-700 text-right" data-testid={`comparison-wac-${item.rm_id}`}>
                        {item.branch_wac != null && item.branch_wac > 0
                          ? `₹${Number(item.branch_wac).toFixed(2)}`
                          : <span className="text-zinc-400">—</span>}
                      </td>
                      <td className="p-4 align-middle text-center">
                        <RMTrendSparkline rmId={item.rm_id} token={token} />
                      </td>
                      <td className="p-4 align-middle">
                        <span className="text-xs font-mono px-2 py-1 bg-primary/10 text-primary rounded">
                          {item.total_vendors} vendor{item.total_vendors > 1 ? 's' : ''}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredComparison.length === 0 && (
                <div className="p-12 text-center text-zinc-500 text-sm">
                  No RM prices found. Add vendors and prices to see comparison.
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* FG Prices Tab (Feb 2026 — Phase 5 Vendor-Site FG) */}
        <TabsContent value="fg-prices">
          <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={fgVendorFilter || "_all"} onValueChange={(v) => { const nv = v === "_all" ? "" : v; setFgVendorFilter(nv); fetchFgPrices(nv, fgSkuFilter); }}>
                <SelectTrigger className="h-9 w-56" data-testid="fg-vendor-filter">
                  <SelectValue placeholder="All vendors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All vendors</SelectItem>
                  {vendors.map(v => (
                    <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                placeholder="Filter by SKU…"
                value={fgSkuFilter}
                onChange={(e) => setFgSkuFilter(e.target.value)}
                className="h-9 w-56 font-mono"
                data-testid="fg-sku-filter"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <PermButton
                perm="VendorFGPrice:CREATE"
                variant="default"
                size="sm"
                onClick={openAddFgPrice}
                className="uppercase text-xs"
                data-testid="add-fg-price-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add FG Price
              </PermButton>
              <Button variant="outline" size="sm" onClick={downloadFgTemplate} className="uppercase text-xs" data-testid="fg-price-template-btn">
                <Download className="w-4 h-4 mr-2" /> Template
              </Button>
              <PermButton
                perm="VendorFGPrice:CREATE"
                variant="outline"
                size="sm"
                onClick={() => fgBulkFileRef.current?.click()}
                className="uppercase text-xs"
                data-testid="fg-price-bulk-upload-btn"
              >
                <Upload className="w-4 h-4 mr-2" /> Bulk upload
              </PermButton>
              <input
                ref={fgBulkFileRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFgBulkUpload}
                className="hidden"
              />
              <Button variant="secondary" size="sm" onClick={exportFgPrices} className="uppercase text-xs" data-testid="export-vendor-fg-prices">
                <Download className="w-4 h-4 mr-2" /> Export
              </Button>
            </div>
          </div>

          {fgUploadResult && (
            <div className="mb-4 border border-border rounded-sm bg-zinc-50 p-3 text-xs">
              <div className="font-semibold">Bulk upload result</div>
              <div className="mt-1 flex gap-4">
                <span>Total: <strong>{fgUploadResult.summary?.total ?? 0}</strong></span>
                <span>Inserted: <strong>{fgUploadResult.summary?.success ?? fgUploadResult.inserted ?? 0}</strong></span>
                <span>Skipped: <strong>{fgUploadResult.summary?.skipped ?? 0}</strong></span>
                <span>Errored: <strong>{fgUploadResult.summary?.errors ?? 0}</strong></span>
              </div>
              {Array.isArray(fgUploadResult.errors_preview) && fgUploadResult.errors_preview.length > 0 && (
                <ul className="mt-2 max-h-32 overflow-auto list-disc list-inside text-zinc-700">
                  {fgUploadResult.errors_preview.slice(0, 25).map((s, i) => (
                    <li key={i} className="font-mono">{typeof s === "string" ? s : JSON.stringify(s)}</li>
                  ))}
                </ul>
              )}
              {fgUploadResult.report_xlsx_b64 && (
                <div className="mt-2 text-zinc-500">A full row-by-row receipt was downloaded automatically.</div>
              )}
            </div>
          )}

          <div className="border border-border bg-white rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-border">
                <tr className="text-left text-xs uppercase text-zinc-600">
                  <th className="p-3">Vendor</th>
                  <th className="p-3">Buyer SKU</th>
                  <th className="p-3">SKU description</th>
                  <th className="p-3">Branch</th>
                  <th className="p-3 text-right">Price</th>
                  <th className="p-3">Currency</th>
                  <th className="p-3">Notes</th>
                  <th className="p-3">Updated</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFgPrices.map(r => (
                  <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`fg-price-row-${r.id}`}>
                    <td className="p-3">{r.vendor_name || <span className="text-zinc-400 font-mono">{r.vendor_id}</span>}</td>
                    <td className="p-3 font-mono text-xs">{r.buyer_sku_id}</td>
                    <td className="p-3 text-xs">{r.buyer_sku_name || <span className="text-zinc-400">—</span>}</td>
                    <td className="p-3 text-xs">{r.branch || <span className="text-zinc-400 italic">Default</span>}</td>
                    <td className="p-3 text-right font-mono">{r.price?.toFixed?.(2) ?? r.price}</td>
                    <td className="p-3 text-xs">{r.currency}</td>
                    <td className="p-3 text-xs text-zinc-600 max-w-[200px] truncate" title={r.notes}>{r.notes}</td>
                    <td className="p-3 text-xs text-zinc-500">{(r.updated_at || "").slice(0, 10)}</td>
                    <td className="p-3 text-right">
                      <div className="flex justify-end gap-1">
                        <PermButton perm="VendorFGPrice:UPDATE" variant="ghost" size="sm" onClick={() => openEditFgPrice(r)} data-testid={`edit-fg-price-${r.id}`}>
                          <Edit className="w-4 h-4" />
                        </PermButton>
                        <PermButton perm="VendorFGPrice:DELETE" variant="ghost" size="sm" onClick={() => handleDeleteFgPrice(r.id)} data-testid={`delete-fg-price-${r.id}`}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </PermButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredFgPrices.length === 0 && (
              <div className="p-8 text-center text-zinc-500 text-sm">
                No FG prices yet. Click <strong>Add FG Price</strong> to create one.
              </div>
            )}
          </div>
        </TabsContent>

      </Tabs>

      {/* RM Vendors Dialog */}
      <Dialog open={showRMVendorsDialog} onOpenChange={setShowRMVendorsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-bold uppercase flex items-center gap-2">
              <Package className="w-5 h-5" />
              Vendors for {selectedRM}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="h-10 px-4 text-left font-mono text-xs font-medium text-zinc-500 uppercase">Vendor</th>
                  <th className="h-10 px-4 text-left font-mono text-xs font-medium text-zinc-500 uppercase">Price</th>
                  <th className="h-10 px-4 text-left font-mono text-xs font-medium text-zinc-500 uppercase">Phone</th>
                </tr>
              </thead>
              <tbody>
                {rmVendors.map((v, idx) => (
                  <tr key={idx} className={`border-b border-zinc-100 ${idx === 0 ? 'bg-green-50' : ''}`}>
                    <td className="p-4 align-middle">
                      <div className="font-bold text-sm">{v.vendor_name}</div>
                      <div className="text-xs text-zinc-500 font-mono">{v.vendor_gst || ''}</div>
                    </td>
                    <td className="p-4 align-middle font-mono text-sm font-bold text-green-600">
                      {v.currency} {v.price.toFixed(2)}
                      {idx === 0 && <span className="ml-2 text-xs bg-green-600 text-white px-1 rounded">LOWEST</span>}
                    </td>
                    <td className="p-4 align-middle text-sm text-zinc-600">
                      {v.vendor_phone || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rmVendors.length === 0 && (
              <div className="p-8 text-center text-zinc-500 text-sm">
                No vendors found for this RM
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add / Edit FG Price Dialog */}
      <Dialog open={showAddFgPriceDialog} onOpenChange={setShowAddFgPriceDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-bold uppercase flex items-center gap-2">
              <Package className="w-5 h-5" />
              {editingFgPrice ? "Edit FG Price" : "Add FG Price"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-3">
            <div>
              <Label className="text-xs uppercase">Vendor</Label>
              <Select
                value={fgPriceForm.vendor_id || "_"}
                onValueChange={(v) => setFgPriceForm(f => ({ ...f, vendor_id: v === "_" ? "" : v }))}
                disabled={!!editingFgPrice}
              >
                <SelectTrigger data-testid="fg-price-vendor-select">
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map(v => (
                    <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase">Buyer SKU</Label>
              <Input
                placeholder="Search buyer SKU…"
                value={fgPriceForm.buyer_sku_id}
                onChange={(e) => setFgPriceForm(f => ({ ...f, buyer_sku_id: e.target.value }))}
                className="font-mono"
                disabled={!!editingFgPrice}
                data-testid="fg-price-sku-input"
                list="fg-sku-list"
              />
              <datalist id="fg-sku-list">
                {availableBuyerSkus.slice(0, 500).map(s => (
                  <option key={s.buyer_sku_id} value={s.buyer_sku_id}>{s.name || ""}</option>
                ))}
              </datalist>
            </div>
            <div>
              <Label className="text-xs uppercase">Branch (blank = vendor-wide default)</Label>
              <Select
                value={fgPriceForm.branch || "_"}
                onValueChange={(v) => setFgPriceForm(f => ({ ...f, branch: v === "_" ? "" : v }))}
                disabled={!!editingFgPrice}
              >
                <SelectTrigger data-testid="fg-price-branch-select">
                  <SelectValue placeholder="Default (vendor-wide)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_">Default (vendor-wide)</SelectItem>
                  {fgAvailableBranches.map(b => (
                    <SelectItem key={b.name} value={b.name}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase">Price</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={fgPriceForm.price}
                  onChange={(e) => setFgPriceForm(f => ({ ...f, price: e.target.value }))}
                  data-testid="fg-price-price-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase">Currency</Label>
                <Input
                  value={fgPriceForm.currency}
                  onChange={(e) => setFgPriceForm(f => ({ ...f, currency: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase">Notes</Label>
              <Textarea
                value={fgPriceForm.notes}
                onChange={(e) => setFgPriceForm(f => ({ ...f, notes: e.target.value }))}
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowAddFgPriceDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveFgPrice} data-testid="fg-price-save-btn">
                {editingFgPrice ? "Update" : "Add"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default VendorManagement;
